/**
 * O idioma da sessão.
 *
 * **É resolvido uma vez, no carregamento do módulo, e não muda.** Trocar de idioma grava a escolha
 * e recarrega a página. Essa decisão é o que mantém o resto pequeno: `t` não precisa de hook nem de
 * provider, os formatadores `Intl` continuam memoizados no topo dos módulos, e some a classe de bug
 * em que um valor derivado dentro de um `useMemo` — ou um `<canvas>` já pintado — fica no idioma
 * anterior depois da troca.
 *
 * O custo é um reload numa ação que se faz uma vez por instalação, a partir de uma tela onde nada
 * está em voo. O seletor no perfil diz isso em vez de esconder.
 */

import type { Locale } from './types';

export type { Locale };

/** A etiqueta que o `Intl` entende. Sem variantes regionais — ver `resolveLocale`. */
const INTL_TAG: Record<Locale, string> = { pt: 'pt-BR', en: 'en-US' };

const LOCALE_KEY = 'mrf.locale';
const FALLBACK: Locale = 'en';

/**
 * Portão de migração — **aberto**.
 *
 * Enquanto foi `false`, quem chegava sem escolha explícita recebia português: o app de hoje,
 * idêntico, enquanto a tradução avançava tela a tela. Agora que o `coverage.test.ts` reporta zero
 * arquivos com texto cru e o seletor existe no perfil, a detecção pelo navegador entra e o fallback
 * passa a ser o inglês.
 *
 * Quem já usava o app **não é afetado**: a primeira visita com `?lang=` ou pelo seletor gravou a
 * escolha, e `storedOverride` vem antes da detecção. Quem nunca escolheu passa a receber o idioma
 * do navegador, que é o comportamento que se espera de um app localizado.
 */
const DETECT_FROM_BROWSER = true;

function isLocale(value: string | null): value is Locale {
  return value === 'pt' || value === 'en';
}

/** `?lang=pt|en` — grava e vale para as próximas páginas. É como se testa e compartilha um link. */
function queryOverride(): Locale | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('lang');
  if (!isLocale(raw)) return null;
  store(raw);
  return raw;
}

function storedOverride(): Locale | null {
  try {
    const raw = localStorage.getItem(LOCALE_KEY);
    return isLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Só a subetiqueta primária: `en-GB` casa com `en`, `pt-PT` casa com `pt`. Um navegador em espanhol
 * não casa com nada e cai no inglês, que é o padrão menos surpreendente.
 */
function detectFromBrowser(): Locale | null {
  if (typeof navigator === 'undefined') return null;
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const primary = tag.toLowerCase().split('-')[0] ?? '';
    if (isLocale(primary)) return primary;
  }
  return null;
}

function store(value: Locale): void {
  try {
    localStorage.setItem(LOCALE_KEY, value);
  } catch {
    /* preferência é best-effort, como em lib/geo/units.ts */
  }
}

/**
 * A ordem é a decisão: **escolha explícita antes de palpite**. `?lang=` ganha de tudo porque é o
 * que se digita para testar ou compartilhar; o que ficou gravado ganha da detecção porque uma
 * escolha feita no perfil não pode ser desfeita por um navegador em outro idioma.
 */
function resolveLocale(): Locale {
  return (
    queryOverride() ??
    storedOverride() ??
    (DETECT_FROM_BROWSER ? detectFromBrowser() : null) ??
    (DETECT_FROM_BROWSER ? FALLBACK : 'pt')
  );
}

export const locale: Locale = resolveLocale();

/**
 * A etiqueta para o `Intl`. **Sem variante regional de propósito**: os únicos consumidores são
 * `NumberFormat`, `Collator` e `PluralRules`, e nenhum deles difere entre `en-GB` e `en-US`. Datas
 * não passam pelo `Intl` — são montadas à mão, com o dia primeiro nos dois idiomas.
 */
export const intlTag: string = INTL_TAG[locale];

/** Um só, compartilhado: substitui os `localeCompare(…, 'pt-BR')` espalhados pelas listas. */
export const collator = new Intl.Collator(intlTag);

/**
 * Envolve cada texto em `⟦…⟧` para revelar o que ficou fora do catálogo.
 *
 * É a **única** verificação completa de cobertura: uma regex acha as strings acentuadas, mas nunca
 * "Voltas", "Campos", "Salvar" ou "Sair". Percorrendo o app com isto ligado, todo texto na tela sem
 * colchetes é uma string esquecida. Só em desenvolvimento.
 */
export const pseudo: boolean =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('lang') === 'pseudo';

/** Grava a escolha e recarrega — ver o comentário do topo. */
export function saveLocale(next: Locale): void {
  store(next);
  if (typeof window !== 'undefined') window.location.reload();
}

// O `lang` do documento acompanha o idioma: é o que orienta a pronúncia do leitor de tela e o
// oferecimento de tradução do navegador. O `index.html` traz `pt-BR` como valor antes do script.
if (typeof document !== 'undefined') {
  document.documentElement.lang = intlTag;
}
